import React, { useEffect, useState } from "react";
import axios from "axios";

import { ButtonNoStyle } from "composants/button/Button";
import { Tooltip } from "@axin-org/comet";
import { Fa } from "composants/Icon";
import { findOne } from "api";
import {
  getAutocompleteCache,
  getCacheKey,
  registerAutocompleteCallback,
  setAutocompleteCache,
} from "composants/autocomplete/autocomplete-cache";
import { ComponentState } from "types/Component";
import { Pojo } from "types/Galaxy";

import { CustomCellRendererProps } from "ag-grid-react";
import { tw } from "twind";

function isEntityValue(value: unknown): value is Pojo {
  return value !== null && typeof value === "object";
}

function AutocompleteCellRendererContent({
  params,
  compo,
  sjmoCode,
}: {
  params: CustomCellRendererProps;
  compo: ComponentState;
  sjmoCode: string;
}) {
  const rawValue = params.value;
  const cacheKey = isEntityValue(rawValue)
    ? null
    : getCacheKey(compo.joinTableName, rawValue != null ? String(rawValue) : null);

  const [resolvedValue, setResolvedValue] = useState<Pojo | unknown>(() => {
    if (isEntityValue(rawValue)) {
      return rawValue;
    }
    if (cacheKey) {
      return getAutocompleteCache(cacheKey) ?? rawValue;
    }
    return rawValue;
  });

  useEffect(() => {
    if (isEntityValue(rawValue)) {
      setResolvedValue(rawValue);
      return;
    }

    if (rawValue == null || rawValue === "" || !compo.joinTableName) {
      setResolvedValue(rawValue);
      return;
    }

    const cached = cacheKey ? getAutocompleteCache(cacheKey) : null;
    if (cached) {
      setResolvedValue(cached);
      return;
    }

    const source = axios.CancelToken.source();
    findOne(
      {
        tableName: compo.joinTableName,
        id: rawValue,
        labelDetailsContext: compo.joinListFields,
        sjmoCode,
      },
      source,
    )
      .then((res) => {
        if (cacheKey) {
          setAutocompleteCache(cacheKey, res.data);
        }
        setResolvedValue(res.data);
      })
      .catch((e) => {
        if (!axios.isCancel(e)) {
          console.error("error during the fetch of the entity with id=", rawValue);
          console.error(e);
        }
      });

    const unregister = registerAutocompleteCallback(cacheKey, () => {
      setResolvedValue(getAutocompleteCache(cacheKey) ?? rawValue);
    });

    return () => {
      source.cancel();
      unregister();
    };
  }, [rawValue, compo.joinTableName, compo.joinListFields, sjmoCode, cacheKey]);

  const displayValue = resolvedValue;

  return (
    <>
      {isEntityValue(displayValue) && displayValue.alerte && (
        <Tooltip
          label={<div dangerouslySetInnerHTML={{ __html: displayValue.alerte }} />}
          portalZindex={500}
        >
          <ButtonNoStyle className={tw("h-4 w-4 left-0 cursor-pointer mx-2")} style={{ zIndex: 5 }}>
            <Fa icon="exclamation-triangle" color="orange" />
          </ButtonNoStyle>
        </Tooltip>
      )}
      {isEntityValue(displayValue) && displayValue.label
        ? displayValue.label
        : isEntityValue(displayValue)
          ? displayValue.id
          : params.value}
    </>
  );
}

export function buildAutocompleteCellRenderer(compo: ComponentState, sjmoCode: string) {
  return (params: CustomCellRendererProps) => (
    <AutocompleteCellRendererContent params={params} compo={compo} sjmoCode={sjmoCode} />
  );
}

export default buildAutocompleteCellRenderer;
